import { inject, Injectable } from '@angular/core';
import { NavigationExtras, Router } from '@angular/router';
import { Location } from '@angular/common';
import { WorkspaceContext } from './workspace-context.service';
import { AdminConnectionContextService } from './admin-connection-context.service';

@Injectable({ providedIn: 'root' })
export class NavigationService {
  private readonly _router = inject(Router);
  private readonly _location = inject(Location);
  private readonly _workspace = inject(WorkspaceContext);
  private readonly _adminConnection = inject(AdminConnectionContextService);

  navigateToSubmitOrder({
    patientId,
    id: treatmentId,
    treatmentType,
    action,
  }: { patientId?: string; id?: string; treatmentType?: number; action?: string } = {}) {
    const connectedUserId: string | null = this._adminConnection.getConnectedUserId();
    const workflowPath: string =
      connectedUserId === null
        ? '/workflow'
        : `/m/admin/connect/${encodeURIComponent(connectedUserId)}/doctor/workflow`;
    return this._router.navigateByUrl(
      `${workflowPath}?patientId=${patientId ?? ''}&referFrom=${this._location.path(true)}&id=${treatmentId ?? ''}&treatmentType=${treatmentType ?? ''}&action=${action ?? ''}`
    );
  }

  navigateToOrderDetail(treatmentId: string) {
    return this._router.navigate([...this._orderListCommands(), 'detail', treatmentId]);
  }

  navigateToOrderList() {
    return this._router.navigate(this._orderListCommands());
  }

  /** Opens a workflow step without leaving the selected administrator connection. */
  public navigateToWorkflowStep(step: string, extras: NavigationExtras = {}): Promise<boolean> {
    const connectedUserId: string | null = this._adminConnection.getConnectedUserId();
    const commands: string[] =
      connectedUserId === null
        ? ['workflow', step]
        : ['m', 'admin', 'connect', connectedUserId, 'doctor', 'workflow', step];
    return this._router.navigate(commands, extras);
  }

  /** Opens a patient record without leaving the selected administrator connection. */
  public navigateToPatientDetail(patientId: string): Promise<boolean> {
    const connectedUserId: string | null = this._adminConnection.getConnectedUserId();
    const commands: string[] =
      connectedUserId === null
        ? ['m', 'patients', 'detail', patientId]
        : ['m', 'admin', 'connect', connectedUserId, 'doctor', 'patients', 'detail', patientId];
    return this._router.navigate(commands, { queryParams: { patientId } });
  }

  /** Opens the patient list without leaving the selected administrator connection. */
  public navigateToPatientList(): Promise<boolean> {
    const connectedUserId: string | null = this._adminConnection.getConnectedUserId();
    const commands: string[] =
      connectedUserId === null ? ['m', 'patients'] : ['m', 'admin', 'connect', connectedUserId, 'doctor', 'patients'];
    return this._router.navigate(commands);
  }

  /**
   * The order list of the workspace being used, which is also the prefix its detail pages hang
   * off. Staying inside one workspace is what lets going back land where the user came from:
   * the other lists are scoped to an office or lab this person may not have, and render empty.
   */
  private _orderListCommands(): string[] {
    const connectedUserId: string | null = this._adminConnection.getConnectedUserId();
    if (connectedUserId !== null) {
      const connectedPrefix: string[] = ['m', 'admin', 'connect', connectedUserId];
      switch (this._workspace.current) {
        case 'admin':
          return [...connectedPrefix, 'administration', 'orders'];
        case 'lab-admin':
          return [...connectedPrefix, 'lab-admin', 'orders'];
        case 'technician':
          return [...connectedPrefix, 'technician', 'orders'];
        default:
          return [...connectedPrefix, 'doctor', 'orders'];
      }
    }

    switch (this._workspace.current) {
      case 'admin':
        return ['m', 'admin', 'orders'];
      case 'lab-admin':
        return ['m', 'lab-admin', 'orders'];
      case 'technician':
        return ['m', 'technician', 'orders'];
      default:
        return ['m', 'orders'];
    }
  }
}
